import React, { useState, useMemo } from 'react';
import { Incident } from '../types/incident';
import { initialCaseTypes } from '../data/caseTypes';
import { initialAgencies } from '../data/agencies';
import { states, getStateName, getRegionName, getDistrictName } from '../data/geography';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { FileText, Map, Activity, AlertTriangle, Calendar, Download, Filter, List } from 'lucide-react';
interface ReportsAnalyticsProps {
  incidents: Incident[];
}
type ReportTab = 'National' | 'Geographic' | 'IncidentType' | 'Casualty' | 'Periodic' | 'IncidentList';
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#ffc658', '#ef5350', '#82ca9d'];
export function ReportsAnalytics({
  incidents
}: ReportsAnalyticsProps) {
  const [activeTab, setActiveTab] = useState<ReportTab>('National');

  // Filter State
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedType, setSelectedType] = useState('');

  // Apply Filters (Only consider approved incidents for official reports)
  const filteredIncidents = useMemo(() => {
    return incidents.filter(i => {
      if (i.status !== 'Approved') return false;
      if (startDate && i.date < startDate) return false;
      if (endDate && i.date > endDate) return false;
      if (selectedState && i.stateId !== selectedState) return false;
      if (selectedType && i.caseTypeId !== selectedType) return false;
      return true;
    });
  }, [incidents, startDate, endDate, selectedState, selectedType]);

  // --- Data Aggregations ---

  // National Summary
  const totalIncidents = filteredIncidents.length;
  const totalDeaths = filteredIncidents.reduce((sum, inc) => sum + inc.numDeaths, 0);
  const totalInjuries = filteredIncidents.reduce((sum, inc) => sum + inc.numInjuries, 0);
  const criticalIncidents = filteredIncidents.filter(i => i.severity === 'Critical').length;

  // By State
  const stateData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredIncidents.forEach(inc => {
      const name = getStateName(inc.stateId);
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count
    })).sort((a, b) => b.count - a.count);
  }, [filteredIncidents]);

  // By Type
  const typeData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredIncidents.forEach(inc => {
      const name = initialCaseTypes.find(t => t.id === inc.caseTypeId)?.name || inc.caseTypeId;
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count
    })).sort((a, b) => b.count - a.count);
  }, [filteredIncidents]);

  // Periodic (Monthly)
  const periodicData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredIncidents.forEach(inc => {
      const month = inc.date.substring(0, 7); // YYYY-MM
      counts[month] = (counts[month] || 0) + 1;
    });
    return Object.entries(counts).map(([month, count]) => ({
      month,
      count
    })).sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredIncidents]);

  // Casualty by Type
  const casualtyData = useMemo(() => {
    const data: Record<string, {
      deaths: number;
      injuries: number;
    }> = {};
    filteredIncidents.forEach(inc => {
      const name = initialCaseTypes.find(t => t.id === inc.caseTypeId)?.name || inc.caseTypeId;
      if (!data[name]) data[name] = {
        deaths: 0,
        injuries: 0
      };
      data[name].deaths += inc.numDeaths;
      data[name].injuries += inc.numInjuries;
    });
    return Object.entries(data).map(([name, stats]) => ({
      name,
      ...stats
    })).sort((a, b) => b.deaths + b.injuries - (a.deaths + a.injuries));
  }, [filteredIncidents]);

  // Geographic Detailed Data
  const geoDetailedData = useMemo(() => {
    const data: Record<string, {
      state: string;
      region: string;
      district: string;
      count: number;
      deaths: number;
      injuries: number;
      critical: number;
    }> = {};
    filteredIncidents.forEach(inc => {
      const state = getStateName(inc.stateId);
      const region = getRegionName(inc.stateId, inc.regionId);
      const district = getDistrictName(inc.stateId, inc.regionId, inc.districtId);
      const key = `${state}-${region}-${district}`;
      if (!data[key]) {
        data[key] = {
          state,
          region,
          district,
          count: 0,
          deaths: 0,
          injuries: 0,
          critical: 0
        };
      }
      data[key].count += 1;
      data[key].deaths += inc.numDeaths;
      data[key].injuries += inc.numInjuries;
      if (inc.severity === 'Critical') data[key].critical += 1;
    });
    return Object.values(data).sort((a, b) => a.state.localeCompare(b.state) || a.region.localeCompare(b.region) || a.district.localeCompare(b.district));
  }, [filteredIncidents]);

  // Incident Type Detailed Data
  const typeDetailedData = useMemo(() => {
    const data: Record<string, {
      name: string;
      count: number;
      deaths: number;
      injuries: number;
      critical: number;
    }> = {};
    filteredIncidents.forEach(inc => {
      const name = initialCaseTypes.find(t => t.id === inc.caseTypeId)?.name || inc.caseTypeId;
      if (!data[name]) {
        data[name] = {
          name,
          count: 0,
          deaths: 0,
          injuries: 0,
          critical: 0
        };
      }
      data[name].count += 1;
      data[name].deaths += inc.numDeaths;
      data[name].injuries += inc.numInjuries;
      if (inc.severity === 'Critical') data[name].critical += 1;
    });
    return Object.values(data).sort((a, b) => b.count - a.count);
  }, [filteredIncidents]);

  // Periodic Detailed Data
  const periodicDetailedData = useMemo(() => {
    const data: Record<string, {
      month: string;
      count: number;
      deaths: number;
      injuries: number;
      critical: number;
    }> = {};
    filteredIncidents.forEach(inc => {
      const month = inc.date.substring(0, 7);
      if (!data[month]) {
        data[month] = {
          month,
          count: 0,
          deaths: 0,
          injuries: 0,
          critical: 0
        };
      }
      data[month].count += 1;
      data[month].deaths += inc.numDeaths;
      data[month].injuries += inc.numInjuries;
      if (inc.severity === 'Critical') data[month].critical += 1;
    });
    return Object.values(data).sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredIncidents]);
  const renderTabNav = () => <div className="flex overflow-x-auto border-b border-gray-200 mb-6" data-id="element-534" data-mp="1e48w789fjcfn">
      <TabButton active={activeTab === 'National'} onClick={() => setActiveTab('National')} icon={<Activity size={16} data-id="element-536" data-mp="1j4n6kwyi6ygu" />} label="National Security" data-id="element-535" data-mp="1e2xwbjj3uott" />
      <TabButton active={activeTab === 'Geographic'} onClick={() => setActiveTab('Geographic')} icon={<Map size={16} data-id="element-538" data-mp="1a25kciium5tu" />} label="State/Regional" data-id="element-537" data-mp="15444e6gpymml" />
      <TabButton active={activeTab === 'IncidentType'} onClick={() => setActiveTab('IncidentType')} icon={<FileText size={16} data-id="element-540" data-mp="2zid9w1hstipf" />} label="Incident Types" data-id="element-539" data-mp="1a2xf5r4mfzj2" />
      <TabButton active={activeTab === 'Casualty'} onClick={() => setActiveTab('Casualty')} icon={<AlertTriangle size={16} data-id="element-542" data-mp="3ek3qh6o4axwd" />} label="Casualty/Impact" data-id="element-541" data-mp="34gycu8ofqghk" />
      <TabButton active={activeTab === 'Periodic'} onClick={() => setActiveTab('Periodic')} icon={<Calendar size={16} data-id="element-544" data-mp="39nd6w9d3tk67" />} label="Periodic Trends" data-id="element-543" data-mp="3eiiwbi5ptgz1" />
      <TabButton active={activeTab === 'IncidentList'} onClick={() => setActiveTab('IncidentList')} icon={<List size={16} data-id="element-546" data-mp="3ilympwr6yl3a" />} label="Incident Report" data-id="element-545" data-mp="3nd6xaw49cci1" />
    </div>;
  return <div className="space-y-6 max-w-7xl mx-auto pb-12" data-id="element-547" data-mp="15guituxvlci">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-548" data-mp="13vyt0ww1wd2">
        <div data-id="element-549" data-mp="3sbi7j7ygsp0s">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-550" data-mp="3sbrwqtboqs5f">Reports & Analytics</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-551" data-mp="a5vgzwjn36hl">Comprehensive analysis of national security incidents</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-blue-800 text-white text-sm font-medium rounded-md hover:bg-blue-900 transition-colors" data-id="element-552" data-mp="62pbws89jlcp">
          <Download size={16} data-id="element-553" data-mp="27x86axmrux50" />
          Export Report PDF
        </button>
      </div>

      {/* Global Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-554" data-mp="2wgbofezbyyx5">
        <div className="flex items-center gap-2 mb-4 text-sm font-medium text-gray-800" data-id="element-555" data-mp="31f67mi17mphs">
          <Filter size={16} className="text-blue-600" data-id="element-556" data-mp="366o8l638gq2r" />
          Report Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-557" data-mp="3a91d5r68uhxy">
          <div data-id="element-558" data-mp="2di8zf06s91zc">
            <label className="block text-xs text-gray-500 mb-1" data-id="element-559" data-mp="2disd24peh47k">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full border border-gray-300 rounded-md text-sm py-1.5 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-560" data-mp="2ihbze67woi0g" />
          </div>
          <div data-id="element-561" data-mp="2rj353b2g4oub">
            <label className="block text-xs text-gray-500 mb-1" data-id="element-562" data-mp="2rjlb2zewuhff">End Date</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full border border-gray-300 rounded-md text-sm py-1.5 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-563" data-mp="1roibr9th5hr" />
          </div>
          <div data-id="element-564" data-mp="guyd8hi8elnu">
            <label className="block text-xs text-gray-500 mb-1" data-id="element-565" data-mp="gxkg43u6gczq">State</label>
            <select value={selectedState} onChange={e => setSelectedState(e.target.value)} className="w-full border border-gray-300 rounded-md text-sm py-1.5 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-566" data-mp="bxevh7mbrqr5">
              <option value="" data-id="element-567" data-mp="32idpqojh5ro">All States</option>
              {states.map(s => <option key={s.id} value={s.id} data-id="element-568" data-mp="3uc7cghr9prk4">{s.name}</option>)}
            </select>
          </div>
          <div data-id="element-569" data-mp="3p9yh5eqxadeu">
            <label className="block text-xs text-gray-500 mb-1" data-id="element-570" data-mp="3pdmbgg46z3um">Incident Type</label>
            <select value={selectedType} onChange={e => setSelectedType(e.target.value)} className="w-full border border-gray-300 rounded-md text-sm py-1.5 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-571" data-mp="3kcyk1tc1wkzv">
              <option value="" data-id="element-572" data-mp="1iizb3emeifqc">All Types</option>
              {initialCaseTypes.map(t => <option key={t.id} value={t.id} data-id="element-573" data-mp="1ejzyd7ufheoc">{t.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-574" data-mp="1m1lw22z52y0h">
        {renderTabNav()}

        {activeTab === 'National' && <div className="space-y-8" data-id="element-575" data-mp="1vxypr0uopbv8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-576" data-mp="1vwxaslajs588">
              <SummaryCard title="Total Approved Incidents" value={totalIncidents} color="blue" data-id="element-577" data-mp="1qznfwq4e31d0" />
              <SummaryCard title="Total Deaths" value={totalDeaths} color="red" data-id="element-578" data-mp="13a2t1w547jyg" />
              <SummaryCard title="Total Injuries" value={totalInjuries} color="orange" data-id="element-579" data-mp="yagenjdjvxo2" />
              <SummaryCard title="Critical Incidents" value={criticalIncidents} color="red" data-id="element-580" data-mp="1cyktgc9hfp1k" />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-581" data-mp="187312cliyws7">
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-582" data-mp="kia1851s84sb">
                <h3 className="text-lg font-medium mb-4" data-id="element-583" data-mp="kggr2fryn2fk">Incidents by State</h3>
                <div className="h-80" data-id="element-584" data-mp="fixdli336too">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-585" data-mp="2r6sk1iiwmhw7">
                    <BarChart data={stateData} layout="vertical" margin={{
                  top: 5,
                  right: 30,
                  left: 40,
                  bottom: 5
                }} data-id="element-586" data-mp="2r5hg9ipgla53">
                      <CartesianGrid strokeDasharray="3 3" data-id="element-587" data-mp="2vzdcafm5wmxm" />
                      <XAxis type="number" data-id="element-588" data-mp="2i7fakpus5tbh" />
                      <YAxis dataKey="name" type="category" width={100} tick={{
                    fontSize: 12
                  }} data-id="element-589" data-mp="2m7pdtur6u5mn" />
                      <Tooltip data-id="element-590" data-mp="39xa99d4j2vct" />
                      <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} data-id="element-591" data-mp="39x1wdl8enq1m" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-592" data-mp="30x51v2zc3wyn">
                <h3 className="text-lg font-medium mb-4" data-id="element-593" data-mp="30yyc0s95ozbe">Incident Types Distribution</h3>
                <div className="h-80" data-id="element-594" data-mp="35xsyq5a8v80s">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-595" data-mp="1ph6y9emxe34t">
                    <PieChart data-id="element-596" data-mp="1pjt1j22s9wh8">
                      <Pie data={typeData} cx="50%" cy="50%" outerRadius={100} fill="#8884d8" dataKey="count" label={({
                    name,
                    percent
                  }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`} data-id="element-597" data-mp="1ugtapluxjfck">
                        {typeData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-598" data-mp="3r8p6c3w96pb9" data-mpi={index} />)}
                      </Pie>
                      <Tooltip data-id="element-599" data-mp="3if3tiq3nfi4h" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>}

        {activeTab === 'Geographic' && <div className="space-y-8" data-id="element-600" data-mp="38ejpjfoqvzik">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-601" data-mp="33h1qch0j4ynr">State & Regional Reports</h2>
            <div className="h-96 border border-gray-200 rounded-lg p-4" data-id="element-602" data-mp="33iuu9ojrd4s2">
               <ResponsiveContainer width="100%" height="100%" data-id="element-603" data-mp="2zh9b5lgmru9f">
                  <BarChart data={stateData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-604" data-mp="11on97k5t7y8e">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-605" data-mp="xp4oaodznn5k" />
                    <XAxis dataKey="name" data-id="element-606" data-mp="2flpy41dceibm" />
                    <YAxis data-id="element-607" data-mp="2fknac2bbgvzx" />
                    <Tooltip data-id="element-608" data-mp="2flg7qcody95e" />
                    <Legend data-id="element-609" data-mp="2kkt0fe1xuafw" />
                    <Bar dataKey="count" name="Incidents" fill="#0ea5e9" data-id="element-610" data-mp="2kjrfmhv4e9kz" />
                  </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg" data-id="element-611" data-mp="2pdvq3b3lgzqt">
              <table className="w-full text-left border-collapse" data-id="element-612" data-mp="2tcblo3dqrgfu">
                <thead data-id="element-613" data-mp="1wnvrtv3lmkwx">
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase" data-id="element-614" data-mp="1wooqs9wbg64i">
                    <th className="px-6 py-3" data-id="element-615" data-mp="21nrw7vg7ked0">State</th>
                    <th className="px-6 py-3" data-id="element-616" data-mp="25mhadq92lzxp">Region</th>
                    <th className="px-6 py-3" data-id="element-617" data-mp="2amlppcy8somc">District</th>
                    <th className="px-6 py-3 text-right" data-id="element-618" data-mp="2ao6j2z8tm1im">Total Incidents</th>
                    <th className="px-6 py-3 text-right" data-id="element-619" data-mp="3h1nqys5y74cv">Deaths</th>
                    <th className="px-6 py-3 text-right" data-id="element-620" data-mp="3m1ihgult9rxe">Injuries</th>
                    <th className="px-6 py-3 text-right" data-id="element-621" data-mp="3m3v5npi5wyiw">Critical</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-622" data-mp="3fli9vlyaeshf">
                  {geoDetailedData.map((s, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-623" data-mp="3tl1ei4rw3loz" data-mpi={idx}>
                      <td className="px-6 py-3 text-sm font-medium text-gray-900" data-id="element-624" data-mp="3tj6vwx43c5o2">{s.state}</td>
                      <td className="px-6 py-3 text-sm text-gray-700" data-id="element-625" data-mp="3plsnh7o8i1x4">{s.region}</td>
                      <td className="px-6 py-3 text-sm text-gray-700" data-id="element-626" data-mp="7ciafh2dsx8p">{s.district}</td>
                      <td className="px-6 py-3 text-sm text-right font-medium" data-id="element-627" data-mp="2e74hhf7j6vr">{s.count}</td>
                      <td className="px-6 py-3 text-sm text-right text-red-600" data-id="element-628" data-mp="h2l3tidf0d7k">{s.deaths}</td>
                      <td className="px-6 py-3 text-sm text-right text-orange-600" data-id="element-629" data-mp="c5upp0l67ros">{s.injuries}</td>
                      <td className="px-6 py-3 text-sm text-right text-red-800" data-id="element-630" data-mp="q5m95gugi0a6">{s.critical}</td>
                    </tr>)}
                  {geoDetailedData.length === 0 && <tr data-id="element-631" data-mp="14sb9fkwj8hhm">
                      <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-632" data-mp="14s2ttl90yy2q">No data available for the selected filters.</td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>}

        {activeTab === 'IncidentType' && <div className="space-y-8" data-id="element-633" data-mp="zu9mgovzjy0v">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-634" data-mp="m0a3xzp95sz8">Incident-Type Reports</h2>
            <div className="h-96 border border-gray-200 rounded-lg p-4" data-id="element-635" data-mp="qr8ml5xpr35n">
               <ResponsiveContainer width="100%" height="100%" data-id="element-636" data-mp="qtbl6ecxm4lz">
                  <BarChart data={typeData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 50
            }} data-id="element-637" data-mp="c46zgn6xhi94">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-638" data-mp="h28hd4smbbj0" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{
                fontSize: 12
              }} data-id="element-639" data-mp="31e8kl1nr8jt" />
                    <YAxis data-id="element-640" data-mp="81ackkacuzzd" />
                    <Tooltip data-id="element-641" data-mp="80hdm5hn1ers" />
                    <Bar dataKey="count" name="Incidents" fill="#8b5cf6" data-id="element-642" data-mp="82tuefbntj39" />
                  </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg" data-id="element-643" data-mp="24yex1tb9ijbp">
              <table className="w-full text-left border-collapse" data-id="element-644" data-mp="1zzll45bmj0dn">
                <thead data-id="element-645" data-mp="1vyrwtau9rjk8">
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase" data-id="element-646" data-mp="1vzbdko86o1wo">
                    <th className="px-6 py-3" data-id="element-647" data-mp="2snzl2qm9w6ru">Incident Type</th>
                    <th className="px-6 py-3 text-right" data-id="element-648" data-mp="2nq7gox4fys6r">Total Incidents</th>
                    <th className="px-6 py-3 text-right" data-id="element-649" data-mp="2nnv4l0ld7c1m">Deaths</th>
                    <th className="px-6 py-3 text-right" data-id="element-650" data-mp="2jn1blst2f71v">Injuries</th>
                    <th className="px-6 py-3 text-right" data-id="element-651" data-mp="2epswk137rj4u">Critical</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-652" data-mp="18a8myqvkmyq1">
                  {typeDetailedData.map((t, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-653" data-mp="138jcbcc9gn2h" data-mpi={idx}>
                      <td className="px-6 py-3 text-sm font-medium text-gray-900" data-id="element-654" data-mp="3exzirhx5wmf9">{t.name}</td>
                      <td className="px-6 py-3 text-sm text-right font-medium" data-id="element-655" data-mp="3jw14ybpvx1uy">{t.count}</td>
                      <td className="px-6 py-3 text-sm text-right text-red-600" data-id="element-656" data-mp="3nye58klv47kc">{t.deaths}</td>
                      <td className="px-6 py-3 text-sm text-right text-orange-600" data-id="element-657" data-mp="3soma2p0lfa1b">{t.injuries}</td>
                      <td className="px-6 py-3 text-sm text-right text-red-800" data-id="element-658" data-mp="1itgklnp49x7">{t.critical}</td>
                    </tr>)}
                  {typeDetailedData.length === 0 && <tr data-id="element-659" data-mp="bh0mm9gg57kf">
                      <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-660" data-mp="bhsi7ka1n5ap">No data available for the selected filters.</td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>}

        {activeTab === 'Casualty' && <div className="space-y-8" data-id="element-661" data-mp="2hah3do8zwm2a">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-662" data-mp="2hbiokljq6oxq">Casualty & Impact Reports</h2>
            <div className="h-96 border border-gray-200 rounded-lg p-4" data-id="element-663" data-mp="1ybclddl8m3dw">
               <ResponsiveContainer width="100%" height="100%" data-id="element-664" data-mp="1tduia3twiidt">
                  <BarChart data={casualtyData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 50
            }} data-id="element-665" data-mp="2760mcrjs4yww">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-666" data-mp="233xa40padqak" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{
                fontSize: 12
              }} data-id="element-667" data-mp="236jig2k049tu" />
                    <YAxis data-id="element-668" data-mp="1afhylo5d5q3z" />
                    <Tooltip data-id="element-669" data-mp="1ag9ul02vhpus" />
                    <Legend verticalAlign="top" data-id="element-670" data-mp="1ah2srcnrn71b" />
                    <Bar dataKey="deaths" name="Deaths" fill="#ef4444" data-id="element-671" data-mp="1of11a1feo98w" />
                    <Bar dataKey="injuries" name="Injuries" fill="#f97316" data-id="element-672" data-mp="1ocwr51qwye06" />
                  </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg" data-id="element-673" data-mp="2zt4wtc1n65g2">
              <table className="w-full text-left border-collapse" data-id="element-674" data-mp="2uw4r528pf4ph">
                <thead data-id="element-675" data-mp="33cdh8qpfaajt">
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase" data-id="element-676" data-mp="33bu9u3xef4k1">
                    <th className="px-6 py-3" data-id="element-677" data-mp="37f0b0x5nm5jn">Incident Type</th>
                    <th className="px-6 py-3 text-right" data-id="element-678" data-mp="2tefte4bh6frq">Deaths</th>
                    <th className="px-6 py-3 text-right" data-id="element-679" data-mp="2tgag9o6b4hyg">Injuries</th>
                    <th className="px-6 py-3 text-right" data-id="element-680" data-mp="2ye27efz4o01l">Total Casualties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-681" data-mp="3m3n1nv0qejq6">
                  {casualtyData.map(c => <tr key={c.name} className="hover:bg-gray-50" data-id="element-682" data-mp="3r40zylcj6fb0">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900" data-id="element-683" data-mp="3cc8wp7q0grpn">{c.name}</td>
                      <td className="px-6 py-3 text-sm text-right text-red-600 font-medium" data-id="element-684" data-mp="3hb3h2e3mmobv">{c.deaths}</td>
                      <td className="px-6 py-3 text-sm text-right text-orange-600 font-medium" data-id="element-685" data-mp="8vjqxny95m11">{c.injuries}</td>
                      <td className="px-6 py-3 text-sm text-right font-bold text-gray-900" data-id="element-686" data-mp="domnm4ybtblg">{c.deaths + c.injuries}</td>
                    </tr>)}
                  {casualtyData.length === 0 && <tr data-id="element-687" data-mp="29bu3htt8zhl2">
                      <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-688" data-mp="1vikys6g8vk8v">No data available for the selected filters.</td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>}

        {activeTab === 'Periodic' && <div className="space-y-8" data-id="element-689" data-mp="1qjrlof4vdv98">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-690" data-mp="1qkt6vcflny4o">Monthly & Periodic Reports</h2>
            <div className="h-96 border border-gray-200 rounded-lg p-4" data-id="element-691" data-mp="1cl1nsxa87rjt">
               <ResponsiveContainer width="100%" height="100%" data-id="element-692" data-mp="1hhs51nxrot6t">
                  <LineChart data={periodicData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-693" data-mp="36xi12i1fw5y6">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-694" data-mp="3bwkzhjmwzdx6" />
                    <XAxis dataKey="month" data-id="element-695" data-mp="3bw2u9xi9xpd4" />
                    <YAxis data-id="element-696" data-mp="3m09pzjxe3tj5" />
                    <Tooltip data-id="element-697" data-mp="3m1kslgf3mv8o" />
                    <Legend data-id="element-698" data-mp="3m0rji8so3tnd" />
                    <Line type="monotone" dataKey="count" name="Incidents" stroke="#0ea5e9" strokeWidth={3} activeDot={{
                r: 8
              }} data-id="element-699" data-mp="3h1ev3jm5eeio" />
                  </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg" data-id="element-700" data-mp="3q1l874dxpchb">
              <table className="w-full text-left border-collapse" data-id="element-701" data-mp="8lmvlbjv0p1e">
                <thead data-id="element-702" data-mp="8js9htwuqqvq">
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase" data-id="element-703" data-mp="8ng0ometr17a">
                    <th className="px-6 py-3" data-id="element-704" data-mp="hm1i2e8k8a6h">Month</th>
                    <th className="px-6 py-3 text-right" data-id="element-705" data-mp="hlia9qcmj266">Total Incidents</th>
                    <th className="px-6 py-3 text-right" data-id="element-706" data-mp="dm15z08xob2l">Deaths</th>
                    <th className="px-6 py-3 text-right" data-id="element-707" data-mp="2jd4tv6u6rusm">Injuries</th>
                    <th className="px-6 py-3 text-right" data-id="element-708" data-mp="2jfhl6alu3hic">Critical</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-709" data-mp="ibgjlzns4iug">
                  {periodicDetailedData.map((p, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-710" data-mp="n9qcu4py19df" data-mpi={idx}>
                      <td className="px-6 py-3 text-sm font-medium text-gray-900" data-id="element-711" data-mp="ncctquyq518b">{p.month}</td>
                      <td className="px-6 py-3 text-sm text-right font-medium" data-id="element-712" data-mp="sbz7d5igsjhn">{p.count}</td>
                      <td className="px-6 py-3 text-sm text-right text-red-600" data-id="element-713" data-mp="x6mqspym2npd">{p.deaths}</td>
                      <td className="px-6 py-3 text-sm text-right text-orange-600" data-id="element-714" data-mp="11886sk6fzi3s">{p.injuries}</td>
                      <td className="px-6 py-3 text-sm text-right text-red-800" data-id="element-715" data-mp="167b0j8gz21wg">{p.critical}</td>
                    </tr>)}
                  {periodicDetailedData.length === 0 && <tr data-id="element-716" data-mp="1g19yrwdxd1j4">
                      <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-717" data-mp="1kyk12cmex7od">No data available for the selected filters.</td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>}

        {activeTab === 'IncidentList' && <div className="space-y-4" data-id="element-718" data-mp="35g2x7isdm8be">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-719" data-mp="35f18wcmcnpbq">Detailed Incident Report</h2>
            <div className="overflow-x-auto border border-gray-200 rounded-lg" data-id="element-720" data-mp="30hrlt3mfnpr2">
              <table className="w-full text-left border-collapse whitespace-nowrap" data-id="element-721" data-mp="2vj6lz5b3f835">
                <thead data-id="element-722" data-mp="3t66pwv410nnm">
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase" data-id="element-723" data-mp="3t5dq6e3hiyez">
                    <th className="px-4 py-3" data-id="element-724" data-mp="3o7lvxdfdb07m">ID</th>
                    <th className="px-4 py-3" data-id="element-725" data-mp="3jfc2hnf1ni0l">Type</th>
                    <th className="px-4 py-3" data-id="element-726" data-mp="3je10nt55skc4">Date</th>
                    <th className="px-4 py-3" data-id="element-727" data-mp="3efzhl87qgl0n">State</th>
                    <th className="px-4 py-3" data-id="element-728" data-mp="fr0kzr36dx1v">Region</th>
                    <th className="px-4 py-3" data-id="element-729" data-mp="fslgpk187m1b">District</th>
                    <th className="px-4 py-3" data-id="element-730" data-mp="azsbyw5rcdp8">Area</th>
                    <th className="px-4 py-3 max-w-xs" data-id="element-731" data-mp="ayzeklsov0jr">Description</th>
                    <th className="px-4 py-3 max-w-xs" data-id="element-732" data-mp="3rmlxbo56s4p6">Remarks</th>
                    <th className="px-4 py-3" data-id="element-733" data-mp="3vp0fe61170fe">Agency</th>
                    <th className="px-4 py-3" data-id="element-734" data-mp="3vql9jujfohcq">Severity</th>
                    <th className="px-4 py-3 text-right" data-id="element-735" data-mp="3hqahio6e0wpz">Cases</th>
                    <th className="px-4 py-3 text-right" data-id="element-736" data-mp="3mokcoys43xbl">Deaths</th>
                    <th className="px-4 py-3 text-right" data-id="element-737" data-mp="3mou3upow8aiv">Injuries</th>
                    <th className="px-4 py-3" data-id="element-738" data-mp="e9tjyizt4868">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-739" data-mp="j6tsd0k4pn0i">
                  {filteredIncidents.map(inc => {
                const typeName = initialCaseTypes.find(t => t.id === inc.caseTypeId)?.name || inc.caseTypeId;
                const agencyName = initialAgencies.find(a => a.id === inc.agencyId)?.name || inc.agencyId;
                return <tr key={inc.id} className="hover:bg-gray-50" data-id="element-740" data-mp="11y33tfdgmluq">
                        <td className="px-4 py-3 text-xs font-medium text-gray-900" data-id="element-741" data-mp="120ftkepgm0ic">{inc.id}</td>
                        <td className="px-4 py-3 text-xs text-gray-700" data-id="element-742" data-mp="2mmnjruk9crnk">{typeName}</td>
                        <td className="px-4 py-3 text-xs text-gray-700" data-id="element-743" data-mp="2hpf1zv30upmu">{inc.date}</td>
                        <td className="px-4 py-3 text-xs text-gray-700" data-id="element-744" data-mp="2vgk5j89ig22g">{getStateName(inc.stateId)}</td>
                        <td className="px-4 py-3 text-xs text-gray-700" data-id="element-745" data-mp="2rg08s5sun0gu">{getRegionName(inc.stateId, inc.regionId)}</td>
                        <td className="px-4 py-3 text-xs text-gray-700" data-id="element-746" data-mp="23s0a8jpaq5rp">{getDistrictName(inc.stateId, inc.regionId, inc.districtId)}</td>
                        <td className="px-4 py-3 text-xs text-gray-700" data-id="element-747" data-mp="2cr3tbnogql67">{inc.location}</td>
                        <td className="px-4 py-3 text-xs text-gray-700 max-w-xs truncate" title={inc.description} data-id="element-748" data-mp="27rr5azluv821">{inc.description}</td>
                        <td className="px-4 py-3 text-xs text-gray-700 max-w-xs truncate" title={inc.reviewNote} data-id="element-749" data-mp="3o4fzi57p3fvj">{inc.reviewNote || '-'}</td>
                        <td className="px-4 py-3 text-xs text-gray-700" data-id="element-750" data-mp="3j90mffhiho06">{agencyName}</td>
                        <td className="px-4 py-3 text-xs text-gray-700" data-id="element-751" data-mp="2gvcj12goz6gz">{inc.severity}</td>
                        <td className="px-4 py-3 text-xs text-right text-gray-700" data-id="element-752" data-mp="2lqi6w6tnmv7p">{inc.numCases}</td>
                        <td className="px-4 py-3 text-xs text-right text-gray-700" data-id="element-753" data-mp="2prtxaekcrkgy">{inc.numDeaths}</td>
                        <td className="px-4 py-3 text-xs text-right text-gray-700" data-id="element-754" data-mp="2uqx2bz0c1qox">{inc.numInjuries}</td>
                        <td className="px-4 py-3 text-xs text-gray-700" data-id="element-755" data-mp="1y0xk1cudrdt5">{inc.status}</td>
                      </tr>;
              })}
                  {filteredIncidents.length === 0 && <tr data-id="element-756" data-mp="2bu5dla20ujdh">
                      <td colSpan={15} className="px-6 py-8 text-center text-sm text-gray-500" data-id="element-757" data-mp="2buf3yyqzasjp">No incidents found matching the selected filters.</td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>}

      </div>
    </div>;
}
function TabButton({
  active,
  onClick,
  icon,
  label
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return <button onClick={onClick} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${active ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-758" data-mp="16t5q5fbtcpvq">
      {icon}
      {label}
    </button>;
}
function SummaryCard({
  title,
  value,
  color
}: {
  title: string;
  value: number;
  color: 'blue' | 'red' | 'orange' | 'green';
}) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    red: 'bg-red-50 text-red-700 border-red-100',
    orange: 'bg-orange-50 text-orange-700 border-orange-100',
    green: 'bg-green-50 text-green-700 border-green-100'
  };
  return <div className={`p-4 rounded-lg border ${colors[color]}`} data-id="element-759" data-mp="55d4e7vxzx3d">
      <h4 className="text-sm font-medium opacity-80 mb-1" data-id="element-760" data-mp="57zjcsl5xevm">{title}</h4>
      <p className="text-3xl font-bold" data-id="element-761" data-mp="1t3vyzssc3iyk">{value}</p>
    </div>;
}